import type { DashboardSaveV2Request, VscProductV2 } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../../components/ui/form";
import { SubsectionHeader } from "../../../../../components/ui/subsectionHeader";
import { formatCurrency } from "../../../../../lib/money";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { useRegisterSaveableSection } from "../../../useRegisterSaveableSection";
import { CheckboxField, CurrencyField, DateField, DropdownField, TextField } from "./Fields";
import type { VscAccess } from "./productAccess";
import { type OriginalRow, ProductOriginalsModal } from "./ProductOriginalsModal";
import { groupViolations } from "./productViolations";
import {
  buildVscDefaultValues,
  FORM_NUMBER_CODES,
  FORM_NUMBER_NOT_LISTED,
  FORM_NUMBER_SELECT_AN_OPTION,
  toVscSaveRequest,
  UNLIMITED_MILE_SENTINEL,
  UNLIMITED_MONTH_SENTINEL,
  vehicleServiceContractInformationSchema,
} from "./VehicleServiceContractInformation.schema";
import { withOption } from "./withOption";

interface VehicleServiceContractInformationProperties {
  /** `additionalProducts.vsc` off the dashboard response. */
  readonly vsc?: VscProductV2;
  /** `additionalProducts.metadata.tpapVsc` — selects the third-party (TPAP) layout. */
  readonly tpapVsc?: boolean;
  /** Resolved per-field editability (spec 6.2); defaults to everything locked. */
  readonly access?: VscAccess;
  /** `additionalProducts.contractVsc` — originally sold values shown in the P3 popup. */
  readonly contractVsc?: VscProductV2;
  readonly contractValuesUnavailable?: boolean;
}

const LOCKED: VscAccess = {
  headerLink: false,
  vendorDropdown: false,
  vendorEnabled: false,
  certificate: false,
  sellingPrice: false,
  terms: false,
  unlimited: false,
  effectiveDate: false,
  formNumber: false,
  anyEditable: false,
};

/** BFF violation field -> this form's field. */
const VIOLATION_FIELDS = {
  vendorId: "tpapVendor",
  vendorName: "tpapVendor",
  certificateNo: "certificateNumber",
  sellingPrice: "sellingPrice",
  termMonths: "termMonths",
  termMiles: "termMiles",
  unlimitedMonths: "termMonths",
  unlimitedMileage: "termMiles",
  effectiveDate: "effectiveDate",
  formNo: "formNumber",
} as const;

/**
 * RHF + Zod, same pattern as ContractVehicleInformation.tsx/InsuranceInformation.tsx
 * — see VehicleServiceContractInformation.schema.ts for the validation rules.
 *
 * Registered with the shared save orchestrator as "vsc", contributing only
 * `additionalProducts.vsc` — the orchestrator merges `additionalProducts` per
 * product, so this doesn't clobber the GAP section's contribution.
 */
export function VehicleServiceContractInformation({
  vsc,
  tpapVsc: isTpapVsc = false,
  access = LOCKED,
  contractVsc,
  contractValuesUnavailable,
}: VehicleServiceContractInformationProperties) {
  const { t } = useTranslation();
  const [originalsOpen, setOriginalsOpen] = useState(false);
  const originalsTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!access.headerLink) {
      setOriginalsOpen(false);
    }
  }, [access.headerLink]);
  const violations = groupViolations(vsc?.validations, VIOLATION_FIELDS);
  const form = useForm({
    resolver: zodResolver(vehicleServiceContractInformationSchema),
    mode: "onBlur",
    defaultValues: buildVscDefaultValues(vsc, isTpapVsc),
  });
  const { control } = form;
  const tpapVsc = useController({ control, name: "tpapVsc" });
  const vendorDisplay = useController({ control, name: "vendorDisplay" });
  const tpapVendor = useController({ control, name: "tpapVendor" });
  const certificateNumber = useController({ control, name: "certificateNumber" });
  const sellingPrice = useController({ control, name: "sellingPrice" });
  const termDisplay = useController({ control, name: "termDisplay" });
  const isUnlimitedMonth = useController({ control, name: "isUnlimitedMonth" });
  const termMonths = useController({ control, name: "termMonths" });
  const isUnlimitedMile = useController({ control, name: "isUnlimitedMile" });
  const termMiles = useController({ control, name: "termMiles" });
  const effectiveDate = useController({ control, name: "effectiveDate" });
  const formNumber = useController({ control, name: "formNumber" });

  const [saveError, setSaveError] = useState<string>();

  useRegisterSaveableSection("vsc", {
    // Pre-approval and locked cards skip the required-but-empty highlighting (spec 5.1).
    validate: async () => !access.anyEditable || form.trigger(),
    getPayload: (): Partial<DashboardSaveV2Request> =>
      // Nothing to write for a locked card, or when the deal has no VSC and isn't a TPAP one.
      !access.anyEditable || !(vsc || isTpapVsc)
        ? {}
        : { additionalProducts: { vsc: toVscSaveRequest(form.getValues(), vsc) } },
    onSaved: () => {
      setSaveError(undefined);
      form.reset(undefined, { keepValues: true, keepDirty: false, keepErrors: true });
    },
    onSaveFailed: (error) => {
      setSaveError(getErrorMessage(error));
    },
  });

  // Vendor changes stay disabled until a lookup supplies writable vendor IDs.
  const tpapVendorOptions = withOption([], vsc?.vendorName).map((vendor) => ({
    value: vendor,
    label: vendor,
  }));
  const formNumberOptions = [
    { value: FORM_NUMBER_SELECT_AN_OPTION, label: t("vehicle.vehicleServiceContract.selectAnOption") },
    ...withOption(FORM_NUMBER_CODES, vsc?.formNo).map((code) => ({ value: code, label: code })),
    { value: FORM_NUMBER_NOT_LISTED, label: t("vehicle.vehicleServiceContract.formNotListed") },
  ];

  // Validates on change rather than blur (like CPO Date) — selecting an
  // option doesn't blur the trigger, so `mode: "onBlur"` alone would never
  // reveal the required error.
  function handleFormNumberChange(value: string) {
    formNumber.field.onChange(value);
    void form.trigger("formNumber");
  }

  // Checking sets Term Months to the "999" sentinel; unchecking wipes
  // whatever was typed back to blank.
  function handleUnlimitedMonthChange(checked: boolean) {
    isUnlimitedMonth.field.onChange(checked);
    termMonths.field.onChange(checked ? UNLIMITED_MONTH_SENTINEL : "");
    void form.trigger("termMonths");
  }

  // Same wipe-on-uncheck behavior as the month checkbox, sentinel "999999".
  function handleUnlimitedMileChange(checked: boolean) {
    isUnlimitedMile.field.onChange(checked);
    termMiles.field.onChange(checked ? UNLIMITED_MILE_SENTINEL : "");
    void form.trigger("termMiles");
  }

  const originalRows: OriginalRow[] = [
    { label: t("vehicle.vehicleServiceContract.vendor"), value: contractVsc?.vendorName },
    {
      label: t("vehicle.vehicleServiceContract.sellingPrice"),
      value: contractVsc?.sellingPrice == null ? undefined : formatCurrency(contractVsc.sellingPrice),
    },
    {
      label: t("vehicle.vehicleServiceContract.termMonths"),
      value: contractVsc?.unlimitedMonths ? t("vehicle.unlimited") : contractVsc?.termMonths?.toString(),
    },
    {
      label: t("vehicle.vehicleServiceContract.termMiles"),
      value: contractVsc?.unlimitedMileage ? t("vehicle.unlimited") : contractVsc?.termMiles?.toString(),
    },
    { label: t("vehicle.vehicleServiceContract.effectiveDate"), value: contractVsc?.effectiveDate },
    { label: t("vehicle.vehicleServiceContract.formNumber"), value: contractVsc?.formNo },
  ];

  return (
    <div>
      <SubsectionHeader>
        {access.headerLink ? (
          <button
            ref={originalsTrigger}
            type="button"
            className="underline underline-offset-2"
            onClick={() => setOriginalsOpen(true)}
          >
            {t("vehicle.vehicleServiceContract.heading")}
          </button>
        ) : (
          t("vehicle.vehicleServiceContract.heading")
        )}
      </SubsectionHeader>
      <ProductOriginalsModal
        open={originalsOpen && access.headerLink}
        onOpenChange={setOriginalsOpen}
        triggerRef={originalsTrigger}
        title={t("vehicle.productOriginals.title", { product: t("vehicle.vehicleServiceContract.heading") })}
        rows={originalRows}
        unavailable={contractValuesUnavailable}
      />
      {violations.general.map((message) => (
        <p key={message} role="alert" className="text-warning text-2xs px-3 pt-2">
          {message}
        </p>
      ))}
      {saveError && (
        <p role="alert" className="text-destructive text-2xs px-3 pt-2">
          {saveError}
        </p>
      )}
      <Form {...form}>
        <form onSubmit={(event) => event.preventDefault()} noValidate>
          <div className="gap-(--spacing-4) px-(--spacing-3) py-(--spacing-3) grid grid-cols-1 sm:grid-cols-2">
            {access.vendorDropdown ? (
              <DropdownField
                label={t("vehicle.vehicleServiceContract.vendor")}
                value={tpapVendor.field.value}
                options={tpapVendorOptions}
                error={violations.byField.tpapVendor}
                disabled
              />
            ) : (
              <TextField
                label={t("vehicle.vehicleServiceContract.vendor")}
                value={vendorDisplay.field.value}
                error={violations.byField.tpapVendor}
                disabled
              />
            )}
            <TextField
              label={t("vehicle.vehicleServiceContract.certificateNumber")}
              value={certificateNumber.field.value}
              onChange={certificateNumber.field.onChange}
              onBlur={certificateNumber.field.onBlur}
              error={certificateNumber.fieldState.error ?? violations.byField.certificateNumber}
              disabled={!access.certificate}
              maxLength={11}
              uppercase
            />
            <CurrencyField
              label={t("vehicle.vehicleServiceContract.sellingPrice")}
              value={sellingPrice.field.value}
              onChange={(value) => sellingPrice.field.onChange(value ?? null)}
              onBlur={sellingPrice.field.onBlur}
              error={sellingPrice.fieldState.error ?? violations.byField.sellingPrice}
              disabled={!access.sellingPrice}
            />
            {!tpapVsc.field.value && (
              <TextField label={t("vehicle.vehicleServiceContract.term")} value={termDisplay.field.value} disabled />
            )}
            {tpapVsc.field.value && (
              <>
                <div className="gap-(--spacing-2) flex flex-col">
                  {isUnlimitedMonth.field.value ? (
                    <TextField label={t("vehicle.vehicleServiceContract.termMonths")} value="" disabled />
                  ) : (
                    <TextField
                      label={t("vehicle.vehicleServiceContract.termMonths")}
                      value={termMonths.field.value}
                      onChange={(value) => termMonths.field.onChange(value.replaceAll(/\D/g, ""))}
                      onBlur={termMonths.field.onBlur}
                      error={termMonths.fieldState.error ?? violations.byField.termMonths}
                      disabled={!access.terms}
                      maxLength={3}
                    />
                  )}
                  <CheckboxField
                    label={t("vehicle.vehicleServiceContract.unlimitedMonth")}
                    checked={isUnlimitedMonth.field.value}
                    onCheckedChange={handleUnlimitedMonthChange}
                    disabled={!access.unlimited}
                  />
                </div>
                <div className="gap-(--spacing-2) flex flex-col">
                  {isUnlimitedMile.field.value ? (
                    <TextField label={t("vehicle.vehicleServiceContract.termMiles")} value="" disabled />
                  ) : (
                    <TextField
                      label={t("vehicle.vehicleServiceContract.termMiles")}
                      value={termMiles.field.value}
                      onChange={(value) => termMiles.field.onChange(value.replaceAll(/\D/g, ""))}
                      onBlur={termMiles.field.onBlur}
                      error={termMiles.fieldState.error ?? violations.byField.termMiles}
                      disabled={!access.terms}
                      maxLength={6}
                    />
                  )}
                  <CheckboxField
                    label={t("vehicle.vehicleServiceContract.unlimitedMile")}
                    checked={isUnlimitedMile.field.value}
                    onCheckedChange={handleUnlimitedMileChange}
                    disabled={!access.unlimited}
                  />
                </div>
              </>
            )}
            <DateField
              label={t("vehicle.vehicleServiceContract.effectiveDate")}
              value={effectiveDate.field.value}
              onChange={effectiveDate.field.onChange}
              onBlur={effectiveDate.field.onBlur}
              error={effectiveDate.fieldState.error ?? violations.byField.effectiveDate}
              disabled={!access.effectiveDate}
            />
            {tpapVsc.field.value && (
              <DropdownField
                label={t("vehicle.vehicleServiceContract.formNumber")}
                value={formNumber.field.value}
                onChange={handleFormNumberChange}
                onBlur={formNumber.field.onBlur}
                error={formNumber.fieldState.error ?? violations.byField.formNumber}
                options={formNumberOptions}
                disabled={!access.formNumber}
              />
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
